import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaSlidersH } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { useAppTheme } from "../../context/AppThemeContext";
import { TUITION_MODULE_DEFINITIONS } from "./tuitionMockData";

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const normalizeModules = (source = {}) => Object.fromEntries(
  TUITION_MODULE_DEFINITIONS.map((module) => [module.key, source?.[module.key] !== false])
);

export default function TuitionModuleCustomizationPage() {
  const { tuitionId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const [institute, setInstitute] = useState(null);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api.get(API_PATHS.tuition.instituteById(tuitionId))
      .then((response) => {
        if (!active) return;
        const payload = unwrap(response);
        const record = payload.institute || payload.tuition || payload;
        setInstitute(record);
        setModules(normalizeModules(record?.enabledModules || record?.enabled_modules || record?.modules || record?.moduleConfig));
      })
      .catch((requestError) => active && setError(requestError?.message || "Unable to load this tuition institute."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [tuitionId]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put(API_PATHS.tuition.instituteModules(tuitionId), normalizeModules(modules));
      toast.success("Feature modules updated.");
      navigate("/super-admin/tuitions", { replace: true });
    } catch (requestError) {
      setError(requestError?.message || "Unable to update feature modules.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`min-h-full px-3 py-5 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-3xl">
        <button type="button" onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800">
          <FaArrowLeft /> Back
        </button>
        <section className={`rounded-3xl border p-5 shadow-sm sm:p-8 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <h1 className="flex items-center gap-2 text-2xl font-black"><FaSlidersH className="text-sky-500" /> Feature Module Customization</h1>
          <p className="mt-1 text-sm text-slate-500">{institute?.name || institute?.instituteName || "Tuition institute"}</p>
          {loading ? <p className="mt-8 text-sm" role="status">Loading module settings…</p> : (
            <>
              {error && <div role="alert" className="mt-5 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm font-semibold text-rose-700">{error}</div>}
              <div className="mt-6 space-y-2.5">
                {TUITION_MODULE_DEFINITIONS.map((module) => {
                  const enabled = modules[module.key] !== false;
                  return (
                    <label key={module.key} className={`flex cursor-pointer items-center justify-between rounded-2xl border p-4 transition ${enabled ? "border-sky-500/50 bg-sky-500/5" : "border-slate-200 opacity-60 dark:border-slate-800"}`}>
                      <span>
                        <span className="block text-sm font-bold">{module.label}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">{module.description}</span>
                      </span>
                      <input type="checkbox" checked={enabled} onChange={() => setModules((current) => ({ ...current, [module.key]: !enabled }))} className="h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                    </label>
                  );
                })}
              </div>
              <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => navigate(-1)} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold dark:border-slate-700">Cancel</button>
                <button type="button" onClick={save} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-sky-700 disabled:opacity-60"><FaSave /> {saving ? "Saving…" : "Save Module Settings"}</button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
